import { BoxGeometry, Color, Group, Mesh } from 'three';
import type { DrivewayConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';

/**
 * Paved approach from one of the lot's fence sides up to the garage
 * door. The slab is a thin pad sitting on top of the lot base plate,
 * lifted by 1 mm to avoid coplanar z-fighting with the lot ground.
 *
 * Geometry: a single box covering the AABB (`xLo..xHi` × `zLo..zHi`).
 * `side` is purely metadata for the fence builder — the slab itself
 * is identical regardless of which fence side it crosses.
 *
 * `surface` is concrete by default. The other presets darken / tint a
 * clone of the same base material rather than introduce fresh kit
 * slots — until the texture pipeline ships, color is the cheapest
 * signal for "different paving than the lawn".
 */
const SLAB_THICKNESS = 0.05;
const LIFT = 0.001;

export function buildDriveway(driveway: DrivewayConfig, kit: MaterialKit): Group {
  const group = new Group();
  group.name = 'Driveway';

  const w = Math.max(0.1, driveway.xHi - driveway.xLo);
  const d = Math.max(0.1, driveway.zHi - driveway.zLo);
  const slab = new Mesh(
    new BoxGeometry(w, SLAB_THICKNESS, d),
    surfaceMaterial(kit, driveway.surface),
  );
  slab.name = 'Slab';
  slab.receiveShadow = true;
  slab.castShadow = false;
  slab.position.set(
    (driveway.xLo + driveway.xHi) / 2,
    SLAB_THICKNESS / 2 + LIFT,
    (driveway.zLo + driveway.zHi) / 2,
  );
  group.add(slab);

  return group;
}

function surfaceMaterial(kit: MaterialKit, surface: DrivewayConfig['surface']) {
  if (surface === 'concrete') return kit.driveway;
  const m = kit.driveway.clone();
  if (surface === 'asphalt') m.color = new Color('#3a3a3c');
  else if (surface === 'pavers') m.color = new Color('#8a7a64');
  m.userData.ownsMaterial = true;
  return m;
}
